import styled from "@emotion/styled";
import { convert } from "@stepfinance/common";
import {
  darkGrey4,
  formatBalance,
  greenSecondary,
  greenTag,
  TagInfo,
  TokenTag,
  transparentWhite,
} from "@stepfinance/react";
import type { FC } from "react";

import { ReactComponent as Arrow } from "../../assets/arrow.svg";
import { ReactComponent as SuccessIcon } from "../../assets/notifications/successIcon.svg";
import { useLanguageContext } from "../../contexts/language/language";
import type { JupiterRoute } from "../../utils/jupiter/api";
import { getTokenByMint } from "../../utils/tokenInfo";
import { NumberLabel } from "../shared";
import { TokenIcon } from "../TokenIcon";
import { InfoDisplayNumber, InfoRowDiv, TradeInfoContainer, TradeInfoRow } from "./EfficiencyInfo";
import type { RouteMarketInfo } from "./RouteSelector";

type FeesInfoProps = {
  route: JupiterRoute;
  stakedAccess: boolean;
};

type StepFeeRowProps = {
  stakedAccess: boolean;
  text: string;
};

type FeeBreakdownRowProps = {
  marketInfos: RouteMarketInfo[];
};

export const FeesInfo: FC<FeesInfoProps> = ({ route, stakedAccess }) => {
  const {
    currentLanguageData: {
      pages: {
        swapLP: { swapFeesInfo },
      },
    },
  } = useLanguageContext();

  return (
    <TradeInfoContainer>
      <span className="title">{swapFeesInfo.title}</span>
      <div className="content">
        <FeeBreakdownRow marketInfos={route.marketInfos} />
        <StepFeeRow text={swapFeesInfo.stepFee} stakedAccess={stakedAccess} />
        <StepFeeBlurb stakedAccess={stakedAccess}>
          {stakedAccess ? swapFeesInfo.youHaveReducedFees : swapFeesInfo.reduceYourFees}
        </StepFeeBlurb>
      </div>
    </TradeInfoContainer>
  );
};

const FeeBreakdownRow: FC<FeeBreakdownRowProps> = ({ marketInfos }) => {
  return (
    <>
      {marketInfos
        .filter((info) => info.platformFee.amount > 0)
        .map((info, i) => {
          const {
            platformFee: { amount, mint },
          } = info;
          const token = getTokenByMint(mint);
          return token ? (
            <TradeInfoRow key={i}>
              <InfoRowDiv>
                <span className="info-text">{info.label}</span>
              </InfoRowDiv>
              <InfoRowDiv>
                <InfoDisplayNumber>{formatBalance(convert(amount, token.decimals), token.decimals)}</InfoDisplayNumber>
                <TokenTag icon={<TokenIcon size={16} mintAddress={mint} />} text={token.symbol} />
              </InfoRowDiv>
            </TradeInfoRow>
          ) : null;
        })}
    </>
  );
};

const StepFeeRow: FC<StepFeeRowProps> = ({ stakedAccess, text }) => {
  return (
    <TradeInfoRow>
      <InfoRowDiv>
        {stakedAccess && <SuccessIcon />}
        <span className="info-text">{text}</span>
      </InfoRowDiv>
      <InfoRowDiv>
        <TagInfo tagType="default" tagColorOverride={stakedAccess ? darkGrey4 : greenTag}>
          <NumberLabel>0.15%</NumberLabel>
        </TagInfo>
        <Arrow width={10} height={10} color={transparentWhite} />
        <TagInfo tagType="default" tagColorOverride={stakedAccess ? greenTag : darkGrey4}>
          <NumberLabel>0.1%</NumberLabel>
        </TagInfo>
      </InfoRowDiv>
    </TradeInfoRow>
  );
};

const StepFeeBlurb = styled.span<{ stakedAccess: boolean }>`
  margin-left: 10px;
  color: ${({ stakedAccess }) => (stakedAccess ? greenSecondary : transparentWhite)};
`;
